/**
 * Convert a transformed object box to integer dimensions while preserving a
 * ratio captured at the beginning of the resize interaction.
 *
 * Both stored dimensions are derived from one integer scale of the captured
 * ratio. This is deliberately different from rounding transformed width and
 * height independently, which can turn a square or a 2:1 object into a
 * rectangle with a different ratio.
 */
export function captureResizeAspectRatio({ width, height, resizeMode } = {}) {
    if (resizeMode !== 'keep_ratio' || !Number.isFinite(width) || !Number.isFinite(height) || height <= 0) {
        return null;
    }
    return width / height;
}

// Recover the smallest useful integer pair for ratios originating from the
// Designer's integer width/height fields. This lets e.g. 2:1 stay exactly
// 2:1 after integer storage, even when a snap proposes an odd width.
function ratioComponents(aspectRatio) {
    let bestNumerator = Math.round(aspectRatio);
    let bestDenominator = 1;
    let bestError = Math.abs(aspectRatio - bestNumerator);
    for (let denominator = 1; denominator <= 1000; denominator += 1) {
        const numerator = Math.max(1, Math.round(aspectRatio * denominator));
        const error = Math.abs(aspectRatio - numerator / denominator);
        if (error < bestError) {
            bestNumerator = numerator;
            bestDenominator = denominator;
            bestError = error;
            if (error < Number.EPSILON * 10) break;
        }
    }

    const gcd = (a, b) => {
        while (b) [a, b] = [b, a % b];
        return a;
    };
    const divisor = gcd(bestNumerator, bestDenominator);
    return {
        widthUnit: bestNumerator / divisor,
        heightUnit: bestDenominator / divisor,
    };
}

export function constrainResizeToAspectRatio({
    x,
    y,
    width,
    height,
    aspectRatio,
    minWidth = 0,
    minHeight = 0,
    anchor = '',
    primaryAxis,
} = {}) {
    if (!Number.isFinite(aspectRatio) || aspectRatio <= 0) return null;
    if (!Number.isFinite(width) || !Number.isFinite(height) || width < 0 || height < 0) return null;

    const minimumWidth = Math.max(0, Number.isFinite(minWidth) ? minWidth : 0);
    const minimumHeight = Math.max(0, Number.isFinite(minHeight) ? minHeight : 0);
    const leftMoves = anchor.includes('left');
    const rightMoves = anchor.includes('right');
    const topMoves = anchor.includes('top');
    const bottomMoves = anchor.includes('bottom');
    const { widthUnit, heightUnit } = ratioComponents(aspectRatio);

    // Corner handles have both axes. For a future/temporarily enabled single
    // axis handle, use the axis that actually moves as the primary dimension.
    const widthIsPrimary = primaryAxis === 'width' ||
        (primaryAxis !== 'height' && (leftMoves || rightMoves || (!topMoves && !bottomMoves)));

    let nextWidth;
    let nextHeight;
    // Select one integer scale from the primary dimension and derive both
    // stored dimensions from the captured ratio. This is one rounding step,
    // not independent width/height rounding.
    let scale = widthIsPrimary
        ? Math.round(width / widthUnit)
        : Math.round(height / heightUnit);
    scale = Math.max(1, scale);
    scale = Math.max(scale, Math.ceil(minimumWidth / widthUnit), Math.ceil(minimumHeight / heightUnit));
    nextWidth = widthUnit * scale;
    nextHeight = heightUnit * scale;

    // Keep the opposite edge fixed for handles that move from the left/top.
    // Rounding the fixed edge itself preserves the existing integer placement
    // semantics while the resize correction changes only the size.
    const rightEdge = x + width;
    const bottomEdge = y + height;
    const nextX = leftMoves ? Math.round(rightEdge) - nextWidth : Math.round(x);
    const nextY = topMoves ? Math.round(bottomEdge) - nextHeight : Math.round(y);

    return {
        x: nextX,
        y: nextY,
        width: nextWidth,
        height: nextHeight,
    };
}
